<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useCssVar, useScroll } from "@vueuse/core";
import VWrapper from "@magnit/core/src/components/VWrapper/VWrapper.vue";
import VNavigation from "@magnit/unit-navigation/src/VNavigation.vue";
import { useAnalyticManager } from "@magnit/analytic-events";
import type { INavigationEmits } from "@magnit/unit-navigation/src/VNavigation.types";
import { MenuItems } from "@magnit/unit-navigation/src/VNavigation.vars";
import VHeaderAuth from "./parts/Auth.vue";
import VHeaderCatalog from "./parts/Catalog.vue";
import VHeaderSearch from "./parts/Search.vue";
import VHeaderBottomMenu from "./parts/BottomMenu.vue";
import type { IHeaderEmits, IHeaderProps } from "./header.types";
import LogoMagnit from "./assets/svg/logo-magnit-color-full.svg";
import LogoCosmetic from "./assets/svg/logo-cosmetic-color-full.svg";
import { phoneForCustomers } from "./header.vars";

const props = withDefaults(defineProps<IHeaderProps>(), {
  phone: phoneForCustomers,
  project: "magnit",
  logoLink: "/",
});

const emit = defineEmits<IHeaderEmits>();

const { send } = useAnalyticManager();

const activeItem = computed(() => {
  switch (props.project) {
    case "cosmetic":
      return MenuItems.COSMETIC;
    default:
      return MenuItems.MAGNIT;
  }
});

const logo = computed(() => {
  switch (props.project) {
    case "cosmetic":
      return LogoCosmetic;
    default:
      return LogoMagnit;
  }
});

const onViewNavigation = () => {
  send("Navigation:Top:View");
};

const openNavigationMore = ref(false);

const onClickNavigation = (element: INavigationEmits["click"][0]) => {
  if (element === "more") {
    openNavigationMore.value = !openNavigationMore.value;
  }
  send(
    `Navigation:Top:Item:${
      element[0].toUpperCase() + element.substring(1)
    }:Click`,
  );
};

const onViewSubmenuToggleNavigation = () => {
  send("Navigation:Top:Item:More:View");
};

const onViewLinkNavigation = (element: string) => {
  send(
    `Navigation:Top:Item:${
      element[0].toUpperCase() + element.substring(1)
    }:View`,
  );
};

const onClickSubmenuNavigation = (
  url: INavigationEmits["submenuClick"][0],
  label: INavigationEmits["submenuClick"][1],
) => {
  send("Navigation:Top:More:Link:Click", {
    link_text: label,
    link_dest: url,
  });
};

const rootRef = ref<HTMLElement | null>(null);
const elWindow = ref<HTMLElement | Window | null>(null);
const windowScroll = useScroll(elWindow);
const mini = ref(false);
const searchFocused = ref(false);

onMounted(() => {
  elWindow.value = window;
});

const toggleSearch = (isOpen: boolean) => {
  searchFocused.value = isOpen;
  emit("update:openSearchSuggest", isOpen);
};

const toggleCatalog = (isOpen: boolean) => {
  emit("update:openCatalog", isOpen);
};

const showOverlay = computed(
  () => props.openSearchSuggest || props.openCatalog,
);

watch(showOverlay, (next) => {
  const cssVar = useCssVar("--header-scrolled-top", rootRef);
  mini.value =
    next && windowScroll.y.value <= Math.abs(parseInt(cssVar.value || "0"));
});

const onClickOverlay = () => {
  if (props.openCatalog) {
    emit("update:openCatalog", false);
  }
  if (props.openSearchSuggest) {
    emit("update:openSearchSuggest", false);
  }
};
</script>

<template>
  <header
    ref="rootRef"
    class="pl-header"
    :class="{
      'pl-header_show-overlay': showOverlay,
      'pl-header_open-navigation-more': openNavigationMore,
      'pl-header_with-submenu': bottomMenuLinks?.length,
      'pl-header_scrolled': windowScroll && windowScroll.y.value > 0,
      'pl-header_mini': mini,
    }"
    v-bind="$attrs"
  >
    <slot name="top" />
    <VNavigation
      :active-item="activeItem"
      :phone="phone"
      :submenu-links="submenuLinks"
      @view="onViewNavigation"
      @click="onClickNavigation"
      @submenu-click="onClickSubmenuNavigation"
      @view-link="onViewLinkNavigation"
      @view-submenu-toggle="onViewSubmenuToggleNavigation"
    />
    <VWrapper
      :indent="{ xs: '12px', s: '20px', m: '32px' }"
      class="pl-header__content-wrapper"
    >
      <div class="pl-header__content">
        <RouterLink
          :to="logoLink"
          class="pl-header__logo"
          :class="{ 'pl-header__logo_search-focused': searchFocused }"
          aria-label="Главная"
        >
          <component :is="logo" />
        </RouterLink>
        <div
          class="pl-header__fields"
          :class="{ 'pl-header__fields_search-focused': searchFocused }"
        >
          <VHeaderCatalog
            :open="openCatalog"
            :project="project"
            class="pl-header__fields__catalog"
            @update:open="toggleCatalog"
          >
            <slot name="catalog" />
          </VHeaderCatalog>
          <VHeaderSearch
            :model-value="searchValue"
            :open-suggest="openSearchSuggest"
            @update:model-value="(e) => emit('update:searchValue', e)"
            @update:open-suggest="toggleSearch"
            @submit="(e) => emit('search:submit', e)"
            @focus="emit('search:focus')"
            @blur="emit('search:blur')"
            @clear="emit('search:clear')"
            @click:cancel="$emit('click:cancel')"
          >
            <slot name="search" />
          </VHeaderSearch>
        </div>
        <div class="pl-header__location">
          <slot name="location" />
        </div>
        <div class="pl-header__buttons">
          <VHeaderAuth
            :authorized="authorized"
            :username="username"
            :premium="premium"
            @click="emit('auth:click')"
          />
        </div>
      </div>
    </VWrapper>
  </header>
  <div
    v-if="bottomMenuLinks?.length"
    class="pl-header-bmenu"
    :class="{
      'pl-header-bmenu_scrolled': windowScroll && windowScroll.y.value > 0,
    }"
  >
    <VHeaderBottomMenu :items="bottomMenuLinks" />
  </div>
  <Transition name="header-search-overlay">
    <div v-if="showOverlay" class="pl-header-overlay" @click="onClickOverlay" />
  </Transition>
</template>

<style scoped lang="postcss">
.pl-header {
  --header-project-nav-height: 68px;
  --header-scrolled-top: -172px;

  position: sticky;
  top: var(--header-scrolled-top);
  z-index: 11;
  background-color: var(--pl-global-background-primary);
  margin-top: 0;
  transition:
    margin-top 0.3s ease-in-out,
    background-color 0.2s ease-in-out 0.3s;

  &&_show-overlay,
  &&_scrolled {
    background-color: var(--pl-global-background-primary);
    transition:
      margin-top 0.3s ease-in-out,
      background-color 0s ease-in-out 0s;
  }

  &_scrolled {
    &:not(.pl-header_with-submenu) {
      border-bottom: 1px solid var(--pl-divider-primary);
    }
  }

  &.pl-header_open-navigation-more {
    top: 0;
  }

  &.pl-header_mini.pl-header_show-overlay {
    margin-top: var(--header-scrolled-top);
  }

  &__content {
    padding: var(--pl-unit-x4) 0 var(--pl-unit-x2);
    display: flex;
    gap: 8px;
    justify-content: space-between;
    flex-wrap: wrap;
    align-items: center;
    position: relative;
    z-index: var(--pl-z-index-level3);

    &-wrapper {
      position: relative;
      z-index: var(--pl-z-index-level3);

      .pl-header_scrolled & {
        background-color: var(--pl-global-background-primary);
      }
    }
  }

  &__logo {
    max-width: 135px;
    order: 1;
    display: flex;
    padding-right: 8px;
    transition: flex-basis 0.2s ease-in-out;

    svg {
      height: 32px;
    }

    &_search-focused {
      @media (--pl-viewport-from-l) {
        display: none;
      }

      @media (--pl-viewport-from-xl) {
        display: flex;
      }
    }
  }

  &__buttons {
    display: flex;
    gap: 8px;
    order: 2;

    /*
      Теперь в кнопке профиля может быть имя пользователя,
      чтобы не врапало этот элемент на новую строку на мобилке,
      нам приходится его явно ограничивать.
    */
    max-width: calc(100% - 157px);
  }

  &__location {
    order: 3;
    width: 100%;
  }

  &__fields {
    order: 4;
    display: flex;
    align-items: center;
    flex-flow: row nowrap;
    gap: 8px;
    flex-basis: 100%;

    &__catalog {
      .pl-header__fields_search-focused & {
        display: none;
      }
    }
  }
}

.pl-header-bmenu {
  position: sticky;
  top: 16px;
  z-index: var(--pl-z-index-level2);

  &_scrolled {
    background-color: var(--pl-global-background-primary);
    border-bottom: 1px solid var(--pl-divider-primary);
  }
}

.pl-header-overlay {
  position: fixed;
  inset: 0;
  height: 100dvh;
  opacity: 1;
  z-index: var(--pl-z-index-over);
  background: var(--pl-overlay-background-default);
}

@media (--pl-viewport-from-m) {
  .pl-header {
    --header-scrolled-top: -116px;

    &__fields {
      &__catalog {
        .pl-header__fields_search-focused & {
          display: block;
        }
      }
    }

    &__location {
      order: 2;
      width: auto;
      margin-left: auto;
    }

    &__buttons {
      order: 3;
    }
  }
}

@media (--pl-viewport-from-l) {
  .pl-header {
    --header-project-nav-height: 66px;
    --header-scrolled-top: -62px;

    background-color: transparent;

    &__content {
      flex-wrap: nowrap;
    }

    &__fields {
      order: 2;
      flex-basis: auto;
      flex-grow: 1;
    }

    &__location {
      order: 3;
    }

    &__buttons {
      order: 4;
    }
  }

  .pl-header-bmenu {
    top: 16px;
  }
}

.header-search-overlay-enter-active,
.header-search-overlay-leave-active {
  transition: opacity 0.3s ease-in-out;
}

.header-search-overlay-enter-from,
.header-search-overlay-leave-to {
  opacity: 0;
}
</style>
